import { WinnerRulesProvider } from 'context/WinnerRulesContext.jsx';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from 'context/AuthContext.jsx';
import SuperAdminPage from 'views/super-admin';
import { ThemeProvider } from 'context/ThemeContext.jsx';
import { DashDataProvider } from 'context/DashDataContext.jsx';
import { UpdatesProvider } from 'context/UpdatesContext.jsx';
import { NotificationsProvider } from 'context/NotificationsContext.jsx';
import { ConfirmProvider } from 'context/ConfirmContext.jsx';
import AppErrorBoundary from 'components/AppErrorBoundary.jsx';
import NeuralField from 'components/NeuralField.jsx';
import AppRoutes from 'routes';

// Management sessions never mount organization data providers or send their
// control token to organization APIs.
function AccountView({ children }) {
  const { isSuperAdmin } = useAuth();
  return isSuperAdmin ? <SuperAdminPage /> : children;
}

/**
 * Provider order matters:
 *   Theme    — independent of everything; writes data-theme before first paint.
 *   Auth     — owns the token and hands it to the transport.
 *   DashData — reacts to sign-in/out.
 *   Updates  — must not fetch while signed out (it would 401).
 *   Notifs   — same rule: it polls an unread count, which 401s while signed out.
 *              Inside Auth so it can watch isSignedIn, and outside the routes so
 *              both shells can read the badge wherever they render it.
 *   Confirm  — renders a Modal, so it sits inside the themed tree.
 */
export default function App() {
  return (
    <AppErrorBoundary label='app'>
      <ThemeProvider>
        <AuthProvider>
          <AccountView>
          <WinnerRulesProvider><DashDataProvider>
            <UpdatesProvider>
              <NotificationsProvider>
                <ConfirmProvider>
                  {/* V3's ambient background. It lives at the app root rather
                      than inside a shell because the only place it is actually
                      visible is the V3 sign-in screen — once signed in,
                      #v3-shell paints an opaque layer over it. */}
                  <NeuralField />
                  <AppRoutes />
                  {/*
                    Toasts carry the reports that used to be modals — see the
                    note in ConfirmContext.

                    Styling is a CLASS, not the inline `style` prop
                    react-hot-toast also offers: inline styles are fixed at
                    mount, and this app has three themes switchable at runtime.
                    A class lets custom.css paint them from the same tokens as
                    everything else, so a toast follows a theme change like
                    every other surface.

                    Top-right keeps them clear of the page's own content, which
                    is centred, while staying in the upper half where the
                    actions that raise them happen — a bottom-corner toast is
                    easy to miss.
                  */}
                  <Toaster
                    position='top-right'
                    toastOptions={{
                      className: 'app-toast',
                      duration: 6000,
                      // Severity through PER-TYPE classes. react-hot-toast
                      // emits no data-attribute for the type, so a
                      // `[data-type=…]` selector would silently never match —
                      // the classes are the only hook it actually gives you.
                      error: { className: 'app-toast app-toast--error' },
                      success: { className: 'app-toast app-toast--success' },
                    }}
                  />
                </ConfirmProvider>
              </NotificationsProvider>
            </UpdatesProvider>
          </DashDataProvider></WinnerRulesProvider>
          </AccountView>
        </AuthProvider>
      </ThemeProvider>
    </AppErrorBoundary>
  );
}
